//#region Imports

import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CreateCoursesWaitlistPayload } from '../../models/payloads/create-course-waitlist.payload';
import { RatingPayload } from '../../models/payloads/rating.payload';
import { CourseWaitlistProxy } from '../../models/proxies/course-waitlist.proxy';
import { CourseProxy } from '../../models/proxies/course.proxy';
import { CrudRequestResponseProxy } from '../../models/proxies/crud-request-response.proxy';
import { GetManyCourseRatingsProxy } from '../../models/proxies/get-many-course-ratings.proxy';
import { GetManyCouseCategoryProxy } from '../../models/proxies/get-many-default-response-course-category.proxy';
import { SubscribedStudents } from '../../models/proxies/subscribed-stutends.proxy';
import { UserCertificateProxy } from '../../models/proxies/user-certificate.proxy';
import { UserCourseTeamProxy } from '../../models/proxies/user-course-team.proxy';
import { AsyncResult } from '../../modules/http-async/models/async-result';
import { HttpAsyncService } from '../../modules/http-async/services/http-async.service';
import { GetManyUserCertificate } from '../../models/proxies/get-many-user-certificate.proxy';
import { CrudRequestParams, createCrudUrl } from '../../utils/crud';

//#endregion

type ManyData<T> = T[] | { data: T[] };
type AsyncManyData<T> = AsyncResult<ManyData<T>>;

@Injectable({
  providedIn: 'root',
})
export class CourseInteractor {

  //#region Constructors

  constructor(
    private readonly http: HttpAsyncService,
  ) { }

  //#endregion

  //#region Public Methods

  public async getAllCourses(
    categoryId: string, searchText: string, currentPage: number, limit: number,
    excludeTrackCourses: boolean = true
  ): Promise<AsyncResult<CourseProxy[] | { data: CourseProxy[] }>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      ...!!searchText && {
        $or: [
          { name: { $contL: searchText } },
          { teacherName: { $contL: searchText } },
          { keywords: { $contL: searchText } },
        ],
      },
      ...categoryId && {
        categoryId: { $eq: categoryId },
      },
    }));

    const baseUrl = environment.api.courses.list;

    let url = baseUrl
      .replace('{searchParams}', searchParams)
      .replace('{page}', String(currentPage))
      .replace('{limit}', String(limit));

    url += '&join[0]=teams';

    if (excludeTrackCourses)
      url += '&excludeTrackCourses=true';

    return await this.http.get<CourseProxy[]>(url);
  }

  public async getMyCourses(
    categoryId?: string, searchText?: string, currentPage?: number, limit?: number,
  ): Promise<AsyncResult<CourseProxy[] | { data: CourseProxy[] }>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      isActive: true,
      ...!!searchText && {
        $or: [
          { name: { $contL: searchText } },
          { teacherName: { $contL: searchText } },
          { keywords: { $contL: searchText } },
        ],
      },
      ...categoryId && {
        categoryId: { $eq: categoryId },
      },
    }));

    const baseUrl = environment.api.courses.myCourses;

    let url = baseUrl;

    if (searchParams)
      url = url.replace('{searchParams}', searchParams);

    url = url.replace('{page}', String(currentPage || 1))
      .replace('{limit}', String(limit || 1000));

    return await this.http.get<CourseProxy[]>(url);
  }

  public async getOtherCourses(
    categoryId: string, searchText: string, currentPage: number, limit: number,
  ): Promise<AsyncResult<CourseProxy[] | { data: CourseProxy[] }>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      ...!!searchText && {
        $or: [
          { name: { $contL: searchText } },
          { teacherName: { $contL: searchText } },
          { keywords: { $contL: searchText } },
        ],
      },
      ...categoryId && {
        categoryId: { $eq: categoryId },
      },
    }));

    const baseUrl = environment.api.courses.otherCourses;

    let url = baseUrl
      .replace('{searchParams}', searchParams)
      .replace('{page}', String(currentPage))
      .replace('{limit}', String(limit));

    return await this.http.get<CourseProxy[]>(url);
  }

  public async getCoursesWithoutCategory(
    currentPage: number, limit: number
  ): Promise<AsyncManyData<CourseProxy>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      categoryId: { $eq: null },
    }));

    const baseUrl = environment.api.courses.list + '&join[0]=teams';

    const url = baseUrl
      .replace('{searchParams}', searchParams)
      .replace('{page}', String(currentPage))
      .replace('{limit}', String(limit));

    return await this.http.get<CourseProxy[]>(url);
  }

  public async getOneCourse(courseId: string, addJoin: boolean = true): Promise<AsyncResult<CourseProxy>> {
    let url = environment.api.courses.get.replace('{courseId}', courseId);

    if (addJoin) url += '?join[0]=modules&join[1]=modules.lessons&join[2]=teams&join[3]=modules.quizzes&join[4]=category';
    
    return await this.http.get<CourseProxy>(url);
  }

  public async downloadCertificate(userId: string, courseId: string): Promise<AsyncResult<Blob>> {
    const url = environment.api.certificates.downloadCertificate
      .replace('{userId}', userId)
      .replace('{subjectId}', courseId);

    return await this.http.getHttpClient()
      .post(url, {}, { responseType: 'blob' })
      .toPromise()
      .then(success => ({ success, error: undefined }))
      .catch(async error => ({ success: undefined, error: { ...error, error: JSON.parse(await error?.error?.text().catch(() => null)) } }));
  }

  public async getPurchasedCourses(): Promise<AsyncResult<CourseProxy[]>> {
    return await this.http.get<CourseProxy[]>(environment.api.courses.purchased);
  }

  public async getSubscriptionByUserAndCourse(
    userId: string, courseId: string
  ): Promise<AsyncManyData<CourseWaitlistProxy>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      userId,
      courseId,
    }));

    const url = environment.api.courses.waitlist.list
      .replace('{searchParams}', searchParams);

    return await this.http.get<ManyData<CourseWaitlistProxy>>(url);
  }

  public async createSubscriptionToCourse(payload: CreateCoursesWaitlistPayload): Promise<AsyncResult<CourseWaitlistProxy>> {
    return await this.http.post<CourseWaitlistProxy>(environment.api.courses.waitlist.create, payload);
  }

  public async deleteSubscriptionToCourse(waitlistId: string): Promise<AsyncResult<CrudRequestResponseProxy<string>>> {
    const url: string = environment.api.courses.waitlist.delete.replace('{id}', waitlistId);
    return await this.http.delete<CrudRequestResponseProxy<string>>(url);
  }

  public async getSubscribedCourseTeamByCourseIdAndUserId(
    userId: string, courseId: string
  ): Promise<AsyncManyData<UserCourseTeamProxy>> {
    const searchParams = encodeURIComponent(JSON.stringify({
      courseId,
      userId,
      isActive: true,
    }));

    const url = environment.api.courses.usersTeams.listSubscribedByCourseAndUserId
      .replace('{searchParams}', searchParams)
      .replace('{limit}', String(1));

    return await this.http.get<ManyData<UserCourseTeamProxy>>(url);
  }

  public async getUserCertificates(userId: string): Promise<AsyncManyData<UserCertificateProxy>> {
    const searchParams: CrudRequestParams<UserCertificateProxy> = {
      search: {
        $or: [ 
          { userId: userId },
          { isActive: true}
        ],
      },
      limit: 15,
      join: ['course'],
    };

    const url = createCrudUrl(environment.api.certificates.getCertificates, searchParams);
    return await this.http.get<ManyData<UserCertificateProxy>>(url + '&showOnlyTaught=false');
  }

  public async getUserCertificatesByCourses(
    userId: string, coursesIds: string[]
  ): Promise<AsyncManyData<UserCertificateProxy>> {
    const searchParams: CrudRequestParams<UserCertificateProxy> = {
      search: {
        $or: [ 
          { userId: userId },
          { isActive: true}
        ],
        courseId: { $in: coursesIds },
      },
      limit: 100,
      join: ['course'],
    };

    const url = createCrudUrl(environment.api.certificates.getCertificates, searchParams);
    return await this.http.get<ManyData<UserCertificateProxy>>(url + '&showOnlyTaught=false');
  }

  public async getUserCertificateByCourseId(userId: string, courseId: string): Promise<AsyncResult<GetManyUserCertificate>> {
    const searchParams: CrudRequestParams<UserCertificateProxy> = {
      search: {
        $or: [ 
          { userId: userId },
          { courseId: courseId },
          { isActive: true}
        ],
      },
    };

    const url = createCrudUrl(environment.api.certificates.getCertificates, searchParams);

    return await this.http.get<GetManyUserCertificate>(url);
  }

  public async getRatingsFromCourse(courseId: string): Promise<AsyncResult<GetManyCourseRatingsProxy>> {
    const searchParams = encodeURIComponent(JSON.stringify({ courseId }));

    const url: string = environment.api.courses.ratings.ratingsFromCourse.replace('{searchParams}', searchParams) + '&join=user';

    return await this.http.get<GetManyCourseRatingsProxy>(url);
  }

  public async sendRating(payload: RatingPayload): Promise<AsyncResult<void>> {
    return await this.http.post<void>(environment.api.courses.ratings.list, payload);
  }

  public async editRating(payload: RatingPayload): Promise<AsyncResult<void>> {
    return await this.http.put<void>(
      environment.api.courses.ratings.update.replace('{id}', payload.id), 
      payload
    );
  }

  public async getSubscribedStudents(courseId: string): Promise<AsyncResult<SubscribedStudents>> {
    const url: string = environment.api.courses.subscribedStudents.replace('{courseId}', courseId);

    return await this.http.get<SubscribedStudents>(url);
  }

  public async getCategoriesSortedByName(): Promise<AsyncResult<GetManyCouseCategoryProxy>> {
    const url: string = environment.api.courses.categories.list + '?sort=name,ASC&join=courses';
    return await this.http.get<GetManyCouseCategoryProxy>(url);
  }

  public async getCoursesGroupedByCategories(
    page: number,
    limit: number,
    numberOfCourses: number,
    categoryId?: string | null,
    searchText?: string,
  ): Promise<AsyncResult<GetManyCouseCategoryProxy>> {
    let url: string = environment
      .api
      .courses
      .categories
      .feed
      .replace('{page}', page.toString())
      .replace('{limit}', limit.toString())
      .replace('{numberOfCourses}', numberOfCourses.toString());

    if (categoryId)
      url += `&categoryId=${categoryId}`;

    if (searchText)
      url += `&searchText=${searchText}`;

    return await this.http.get<GetManyCouseCategoryProxy>(url);
  }

  //#endregion

}
